import { useCallback, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import ShotContext from "components/Shot/ShotContext";
import { SERVICES, WINDOW_EVENTS } from "constants/constants";

/**
 * Opens Smart Vectors annotation (ROI) flow: same behavior as the former
 * dropdown “Add Region of Interest” action.
 */
export function useSmartVectorsAddRoi(): () => void {
  const {
    project,
    service,
    layerId,
    layers,
    selectedShot,
    setSelectedLayer,
    setSmartVectorsReviewPane,
    setReviewLayer,
    shotCutouts,
    requestSmartVectorsAddRoiRef,
  } = useContext(ShotContext);
  const navigate = useNavigate();

  const syncLayerFromRoute = useCallback(() => {
    if (layerId) {
      const layer = layers?.find((l) => l.id === +layerId);
      if (layer) setSelectedLayer(layer);
    }
  }, [layerId, layers, setSelectedLayer]);

  const goToOriginalVideoPreviewThen = useCallback(
    (then: () => void, options?: { keepSelectedLayer?: boolean }) => {
      if (!selectedShot || !project) return;
      if (!layerId) {
        then();
        return;
      }
      navigate(`/projects/${project.id}/${service}/shots/${selectedShot.id}`);
      const preserve = options?.keepSelectedLayer && layerId && layers?.length;
      if (preserve) {
        const layer = layers.find((l) => l.id === +layerId);
        if (layer) setSelectedLayer(layer);
      } else {
        setSelectedLayer(null);
      }
      setSmartVectorsReviewPane(null);
      setReviewLayer(null);
      setTimeout(then, 150);
    },
    [
      layerId,
      layers,
      navigate,
      project,
      selectedShot,
      service,
      setSelectedLayer,
      setReviewLayer,
      setSmartVectorsReviewPane,
    ],
  );

  const resolveAbsoluteFrameFromReviewUi = useCallback((): Promise<number> => {
    return new Promise((resolve) => {
      if (!selectedShot) {
        resolve(1);
        return;
      }

      let fallbackTimer: ReturnType<typeof setTimeout>;

      const onReply = (e: Event) => {
        window.removeEventListener(WINDOW_EVENTS.CURRENT_FRAME_REPLY, onReply);
        clearTimeout(fallbackTimer);
        const rel = (e as CustomEvent).detail?.frame;
        if (rel !== undefined) {
          resolve(rel + selectedShot.first_frame_number);
        } else {
          resolve(selectedShot.first_frame_number);
        }
      };

      fallbackTimer = setTimeout(() => {
        window.removeEventListener(WINDOW_EVENTS.CURRENT_FRAME_REPLY, onReply);
        const cutout = layerId
          ? shotCutouts.find((c) => c.layer_id === +layerId)
          : undefined;
        resolve(cutout?.frame ?? selectedShot.first_frame_number);
      }, 200);

      window.addEventListener(WINDOW_EVENTS.CURRENT_FRAME_REPLY, onReply);
      window.dispatchEvent(new Event(WINDOW_EVENTS.GET_CURRENT_FRAME));
    });
  }, [layerId, selectedShot, shotCutouts]);

  const addRegionOfInterest = useCallback(() => {
    if (!selectedShot) return;

    if (!layerId) {
      syncLayerFromRoute();
      window.dispatchEvent(new CustomEvent(WINDOW_EVENTS.ANNOTATE));
      return;
    }

    const currentLayer = layers?.find((l) => l.id === +layerId);
    const keepSelectedLayer =
      !!currentLayer && !currentLayer.latest_inference_cutout_status;

    resolveAbsoluteFrameFromReviewUi().then((absoluteFrame) => {
      goToOriginalVideoPreviewThen(
        () => {
          window.dispatchEvent(
            new CustomEvent(WINDOW_EVENTS.ANNOTATE, {
              detail: { frame: absoluteFrame },
            }),
          );
        },
        { keepSelectedLayer },
      );
    });
  }, [
    goToOriginalVideoPreviewThen,
    layerId,
    layers,
    resolveAbsoluteFrameFromReviewUi,
    selectedShot,
    syncLayerFromRoute,
  ]);

  useEffect(() => {
    if (service !== SERVICES.SMART_VECTORS) return undefined;
    const onAddRoi = () => {
      const request = requestSmartVectorsAddRoiRef.current;
      if (request) request();
      else addRegionOfInterest();
    };
    window.addEventListener(WINDOW_EVENTS.SMART_VECTORS_ADD_ROI, onAddRoi);
    return () =>
      window.removeEventListener(WINDOW_EVENTS.SMART_VECTORS_ADD_ROI, onAddRoi);
  }, [addRegionOfInterest, requestSmartVectorsAddRoiRef, service]);

  return addRegionOfInterest;
}
